import React, { useState, useEffect, useRef } from 'react';
import { User, Award, Save, AlertCircle, Search, Filter, ChevronDown, BookOpen, Calculator, X, UserCog, Users } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import { url } from '../../utils/confserveur';
import Loading from '../general/Loading';
import useUpdateGroupe from '../../hooks/update_api_hook/useUpdateGroupe';
import useConfirm from '../../hooks/ui/useConfirm';
// react-router 6.18 n'expose que la version unstable_ du blocker (stabilisée
// en `useBlocker` à partir de 6.19). Import DIRECT : si un upgrade la
// renomme, la compilation casse bruyamment ici, c'est voulu — remplacer
// alors par `useBlocker`.
import { useNavigate, unstable_useBlocker as useBlockerCompat } from 'react-router-dom';

const ProfesseurManagement = () => {
  const navigate = useNavigate();

  // État pour stocker les professeurs
  const [professeurs, setProfesseurs] = useState([]);
  const [modifiedProfesseurs, setModifiedProfesseurs] = useState({});
  const [modifiedDelegues, setModifiedDelegues] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMatiere, setFilterMatiere] = useState("tous");
  const [filterRole, setFilterRole] = useState("tous"); // "tous", "coordonnateur", "delegue", "coordonnateur_delegue"
  const { updateGroupe, isLoading } = useUpdateGroupe();
  const { etablissementData } = useUser();
  const { confirm, ConfirmDialog } = useConfirm();
  // Vrai pendant le rechargement volontaire post-succès : neutralise les
  // gardes de sortie (sinon notre propre navigate(0) déclencherait l'alerte)
  const sauvegardeReussieRef = useRef(false);

  // Charger les professeurs depuis etablissementData
  useEffect(() => {
    if (etablissementData && etablissementData.professeurs) {
      setProfesseurs(etablissementData.professeurs);
    }
  }, [etablissementData]);

  // Fonction pour obtenir les noms des classes
  const getClassNames = (classIds) => {
    if (!etablissementData || !etablissementData.classes) return [];
    return classIds.map(id => {
      const classe = etablissementData.classes.find(c => c.id === id);
      return classe ? classe.nom : '';
    }).filter(nom => nom);
  };

  // Basculer un statut en partant de l'état AFFICHÉ (modif en attente
  // comprise), pas de l'état sauvé : l'ancien code inversait toujours la
  // valeur enregistrée, donc le 2e clic ne faisait RIEN (impossible
  // d'annuler sans recharger). Retour à la valeur d'origine → l'entrée
  // sort de la liste des modifs (badge éteint, bouton Valider re-grisé).
  const basculerStatut = (setModifs, valeurSauvee, profId) => {
    setModifs(prev => {
      const courant = prev[profId] !== undefined ? prev[profId] : valeurSauvee;
      const nouveau = !courant;
      if (nouveau === valeurSauvee) {
        const { [profId]: _retire, ...reste } = prev;
        return reste;
      }
      return { ...prev, [profId]: nouveau };
    });
  };

  const toggleCoordonnateur = (profId) => {
    const prof = professeurs.find(p => p.id === profId);
    basculerStatut(setModifiedProfesseurs, prof.coordonateur, profId);
  };

  const toggleDelegue = (profId) => {
    const prof = professeurs.find(p => p.id === profId);
    basculerStatut(setModifiedDelegues, prof.delegue_affectation ?? false, profId);
  };

  // Fonction pour sauvegarder les changements
  const handleSave = async () => {
    const hasCoordChanges = Object.keys(modifiedProfesseurs).length > 0;
    const hasDelegueChanges = Object.keys(modifiedDelegues).length > 0;

    if (hasCoordChanges || hasDelegueChanges) {
      const nbModifs = Object.keys(modifiedProfesseurs).length + Object.keys(modifiedDelegues).length;

      // Confirmation AVANT : les désignations conditionnent des accès réels
      // (menu coordonnateur, import établissement du délégué)
      const ok = await confirm({
        title: 'Valider les changements ?',
        message: `${nbModifs} désignation${nbModifs > 1 ? 's' : ''} de rôle (coordonnateur ou délégué) vont être mises à jour.`,
        confirmLabel: 'Valider',
        variant: 'warning',
      });
      if (!ok) return;

      const payload = {};

      if (hasCoordChanges) {
        const coordonnateurs = Object.entries(modifiedProfesseurs).map(([profId, status]) => ({
          id: parseInt(profId),
          coordonateur: status
        }));
        payload.coordonnateurs = coordonnateurs;
      }

      if (hasDelegueChanges) {
        const delegues = Object.entries(modifiedDelegues).map(([profId, status]) => ({
          id: parseInt(profId),
          delegue_affectation: status
        }));
        payload.delegues_affectation = delegues;
      }

      const urlupdate = `${url}professeurs/update-coordonnateurs/`;
      // updateGroupe THROW en cas d'échec : sans le try/catch, l'erreur
      // s'échappait en silence et l'écran restait muet (ni message ni reset)
      try {
        await updateGroupe(urlupdate, payload);
        setModifiedProfesseurs({});
        setModifiedDelegues({});
        alert('Désignations enregistrées.');
        sauvegardeReussieRef.current = true; // le reload qui suit est volontaire
        navigate(0);
      } catch (e) {
        alert("L'enregistrement a échoué : vos désignations n'ont pas été appliquées. Réessayez.");
      }
    }
  };

  // Filtrage des professeurs
  const filteredProfesseurs = professeurs.filter(prof => {
    const matchesSearch = prof.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prof.prenom.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMatiere = filterMatiere === "tous" || prof.matiere.toLowerCase() === filterMatiere.toLowerCase();

    // Filtre par rôle (coordonnateur / délégué)
    const isCoord = modifiedProfesseurs[prof.id] !== undefined ? modifiedProfesseurs[prof.id] : prof.coordonateur;
    const isDelegue = modifiedDelegues[prof.id] !== undefined ? modifiedDelegues[prof.id] : (prof.delegue_affectation ?? false);

    let matchesRole = true;
    if (filterRole === "coordonnateur") {
      matchesRole = isCoord;
    } else if (filterRole === "delegue") {
      matchesRole = isDelegue;
    } else if (filterRole === "coordonnateur_delegue") {
      matchesRole = isCoord || isDelegue;
    }

    // Piège du filtre : une carte porteuse d'une modification EN ATTENTE
    // reste visible quel que soit le filtre de rôle — sinon « Retirer »
    // sous le filtre Coordonnateurs escamotait la carte (et son bouton
    // Annuler) avant même l'enregistrement.
    const porteUneModif = modifiedProfesseurs[prof.id] !== undefined || modifiedDelegues[prof.id] !== undefined;
    matchesRole = matchesRole || porteUneModif;

    return matchesSearch && matchesMatiere && matchesRole;
  });

  // Compter les coordonnateurs par matière
  const countCoordonnateurs = (matiere) => {
    return professeurs.filter(p => p.matiere.toLowerCase() === matiere.toLowerCase() && (
      modifiedProfesseurs[p.id] !== undefined ? modifiedProfesseurs[p.id] : p.coordonateur
    )).length;
  };

  // Compter les délégués d'affectation par matière
  const countDelegues = (matiere) => {
    return professeurs.filter(p => p.matiere.toLowerCase() === matiere.toLowerCase() && (
      modifiedDelegues[p.id] !== undefined ? modifiedDelegues[p.id] : (p.delegue_affectation ?? false)
    )).length;
  };

  const hasChanges = Object.keys(modifiedProfesseurs).length > 0 || Object.keys(modifiedDelegues).length > 0;

  // ── Gardes de sortie : des désignations cochées mais non validées se
  // perdaient sans avertissement (retour guide). Deux couvertures :
  // navigation SPA (blocker du router → modale maison) et fermeture ou
  // rechargement du navigateur (dialogue natif beforeunload).
  const blocker = useBlockerCompat(hasChanges);
  useEffect(() => {
    if (!blocker || blocker.state !== 'blocked') return;
    (async () => {
      const ok = await confirm({
        title: 'Quitter sans valider ?',
        message: 'Vos désignations modifiées seront perdues si vous quittez sans cliquer sur « Valider les changements ».',
        confirmLabel: 'Quitter sans valider',
        variant: 'danger',
      });
      if (ok) blocker.proceed();
      else blocker.reset();
    })();
  }, [blocker, confirm]);

  useEffect(() => {
    if (!hasChanges) return undefined;
    const surFermeture = (e) => {
      if (sauvegardeReussieRef.current) return; // reload volontaire post-succès
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', surFermeture);
    return () => window.removeEventListener('beforeunload', surFermeture);
  }, [hasChanges]);

  // Fonction pour formater la matière
  const formatMatiere = (matiere) => {
    if (matiere.toLowerCase() === 'maths') return 'Mathématiques';
    if (matiere.toLowerCase() === 'français') return 'Français';
    return matiere;
  };

  // Fonction pour annuler tous les changements
  const cancelChanges = () => {
    setModifiedProfesseurs({});
    setModifiedDelegues({});
  };

  if (isLoading) {
    return <Loading loadingText={"Mise à jour des enseignants..."} />
  }

  // Couleurs du thème (bleu comme ImportEleve)
  const colors = {
    gradient: 'from-blue-600 via-cyan-500 to-sky-400',
    blob1: '#67e8f9',
    blob2: '#a5f3fc',
  };

  return (
    <div className="min-h-screen w-full flex justify-center bg-gradient-to-br  py-6">
      <div className="w-fit  bg-white rounded-3xl shadow-xl pb-6 overflow-hidden">
        <div className="">

        {/* Header moderne avec gradient et blobs animés */}
        <div className="relative mb-8 xl:mb-5 2xl:mb-6 3xl:mb-8 overflow-hidden rounded-t-2xl sm:rounded-t-3xl shadow-2xl">
          {/* Glow effect behind header */}
          <div className={`absolute -inset-2 bg-gradient-to-r ${colors.gradient} rounded-3xl blur-2xl opacity-30 animate-pulse`} />

          <div className="relative overflow-hidden">
            {/* Background gradient */}
            <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient}`} />

            {/* Animated liquid blobs */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 200" preserveAspectRatio="none">
              <defs>
                <linearGradient id="header-blob1-prof" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={colors.blob1} stopOpacity="0.5" />
                  <stop offset="100%" stopColor={colors.blob2} stopOpacity="0.2" />
                </linearGradient>
                <linearGradient id="header-blob2-prof" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={colors.blob2} stopOpacity="0.4" />
                  <stop offset="100%" stopColor={colors.blob1} stopOpacity="0.1" />
                </linearGradient>
              </defs>

              {/* First animated blob */}
              <path fill="url(#header-blob1-prof)">
                <animate attributeName="d" dur="8s" repeatCount="indefinite"
                  values="M0,100 Q200,40 400,80 Q600,130 800,70 L800,200 L0,200 Z;
                          M0,70 Q200,110 400,50 Q600,80 800,120 L800,200 L0,200 Z;
                          M0,100 Q200,40 400,80 Q600,130 800,70 L800,200 L0,200 Z" />
              </path>

              {/* Second animated blob */}
              <path fill="url(#header-blob2-prof)">
                <animate attributeName="d" dur="6s" repeatCount="indefinite"
                  values="M0,130 Q300,80 500,110 Q700,140 800,90 L800,200 L0,200 Z;
                          M0,90 Q300,130 500,80 Q700,100 800,140 L800,200 L0,200 Z;
                          M0,130 Q300,80 500,110 Q700,140 800,90 L800,200 L0,200 Z" />
              </path>

              {/* Floating particles */}
              <circle cx="150" cy="80" r="3" fill="white" opacity="0.4">
                <animate attributeName="cy" dur="3s" values="80;50;80" repeatCount="indefinite" />
              </circle>
              <circle cx="650" cy="100" r="2.5" fill="white" opacity="0.3">
                <animate attributeName="cy" dur="4s" values="100;60;100" repeatCount="indefinite" />
              </circle>
              <circle cx="400" cy="70" r="3" fill="white" opacity="0.35">
                <animate attributeName="cy" dur="3.5s" values="70;40;70" repeatCount="indefinite" />
              </circle>
            </svg>

            {/* Content layer */}
            <div className="relative px-6 sm:px-8 xl:px-5 2xl:px-6 3xl:px-8 py-8 sm:py-10 xl:py-6 2xl:py-8 3xl:py-10 text-center">
              {/* Icon */}
              <div className="flex justify-center mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                <div className="p-3 sm:p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-white/20 backdrop-blur rounded-2xl border border-white/30 shadow-xl">
                  <Users className="w-8 h-8 sm:w-10 sm:h-10 xl:w-6 xl:h-6 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 text-white" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl font-bold text-white mb-2 xl:mb-1.5 2xl:mb-2 3xl:mb-2">
                Gestion des Professeurs
              </h1>
              <p className="text-white/80 text-sm sm:text-base md:text-lg xl:text-xs 2xl:text-sm 3xl:text-lg">
                {etablissementData?.nom || 'Votre établissement'} • {professeurs.length} professeur{professeurs.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Statistiques avec glassmorphism */}
        <div className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 grid grid-cols-1 md:grid-cols-2 gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4 mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
          {/* Stat Français */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300" />
            <div className="relative bg-white/70 backdrop-blur-xl border border-white/50 rounded-2xl p-5 xl:p-3.5 2xl:p-4 3xl:p-5 shadow-lg">
              <div className="flex items-center justify-between mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  <div className="p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl shadow-lg shadow-blue-500/30">
                    <BookOpen className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-500 font-medium">Français</p>
                    <p className="text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-slate-800">{countCoordonnateurs("français")} <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-normal text-slate-500">coord.</span></p>
                  </div>
                </div>
                {countCoordonnateurs("français") > 1 && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 rounded-full">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-700">Multiple</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 pt-3 xl:pt-2 2xl:pt-2.5 3xl:pt-3 border-t border-slate-200/50">
                <div className="p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg">
                  <UserCog className="w-4 h-4 xl:w-3.5 xl:h-3.5 2xl:w-3.5 2xl:h-3.5 3xl:w-4 3xl:h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-slate-500">Délégués d'affectation</p>
                  <p className="text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-bold text-emerald-600">{countDelegues("français")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stat Maths */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300" />
            <div className="relative bg-white/70 backdrop-blur-xl border border-white/50 rounded-2xl p-5 xl:p-3.5 2xl:p-4 3xl:p-5 shadow-lg">
              <div className="flex items-center justify-between mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  <div className="p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg shadow-purple-500/30">
                    <Calculator className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-500 font-medium">Mathématiques</p>
                    <p className="text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-slate-800">{countCoordonnateurs("maths")} <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-normal text-slate-500">coord.</span></p>
                  </div>
                </div>
                {countCoordonnateurs("maths") > 1 && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 rounded-full">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-700">Multiple</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 pt-3 xl:pt-2 2xl:pt-2.5 3xl:pt-3 border-t border-slate-200/50">
                <div className="p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg">
                  <UserCog className="w-4 h-4 xl:w-3.5 xl:h-3.5 2xl:w-3.5 2xl:h-3.5 3xl:w-4 3xl:h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-slate-500">Délégués d'affectation</p>
                  <p className="text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-bold text-emerald-600">{countDelegues("maths")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres avec glassmorphism */}
        <div className=" px-4 xl:px-3 2xl:px-3.5 3xl:px-4 relative mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-slate-200 to-slate-300 rounded-2xl blur opacity-30" />
          <div className="relative bg-white/80 backdrop-blur-xl border border-white/50 rounded-2xl p-4 xl:p-3 2xl:p-3.5 3xl:p-4 shadow-lg">
            <div className="flex flex-col sm:flex-row gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 xl:left-3 2xl:left-3.5 3xl:left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                <input
                  type="text"
                  placeholder="Rechercher un professeur..."
                  className="w-full pl-12 xl:pl-10 2xl:pl-11 3xl:pl-12 pr-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-sm sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-300 transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="relative">
                <Filter className="absolute left-4 xl:left-3 2xl:left-3.5 3xl:left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                <select
                  className="w-full sm:w-auto pl-12 xl:pl-10 2xl:pl-11 3xl:pl-12 pr-10 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-sm sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-300 appearance-none cursor-pointer transition-all"
                  value={filterMatiere}
                  onChange={(e) => setFilterMatiere(e.target.value)}
                >
                  <option value="tous">Toutes les matières</option>
                  <option value="français">Français</option>
                  <option value="maths">Mathématiques</option>
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 pointer-events-none" />
              </div>

              <div className="relative">
                <Award className="absolute left-4 xl:left-3 2xl:left-3.5 3xl:left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                <select
                  className="w-full sm:w-auto pl-12 xl:pl-10 2xl:pl-11 3xl:pl-12 pr-10 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-sm sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-300 appearance-none cursor-pointer transition-all"
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                >
                  <option value="tous">Tous les rôles</option>
                  <option value="coordonnateur">Coordonnateurs</option>
                  <option value="delegue">Délégués</option>
                  <option value="coordonnateur_delegue">Coord. ou Délég.</option>
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Grid de Cards Professeurs */}
        {filteredProfesseurs.length === 0 ? (
          <div className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-slate-200 to-slate-300 rounded-2xl blur opacity-30" />
            <div className="relative bg-white/80 backdrop-blur-xl border border-white/50 rounded-2xl p-12 xl:p-8 2xl:p-10 3xl:p-12 text-center shadow-lg">
              <User className="w-12 h-12 xl:w-9 xl:h-9 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12 text-slate-300 mx-auto mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4" />
              <p className="text-slate-500 text-lg xl:text-sm 2xl:text-base 3xl:text-lg">Aucun professeur trouvé</p>
            </div>
          </div>
        ) : (
          <div className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 xl:gap-3 2xl:gap-4 3xl:gap-5">
            {filteredProfesseurs.map(prof => {
              const isModifiedCoord = modifiedProfesseurs[prof.id] !== undefined;
              const isModifiedDelegue = modifiedDelegues[prof.id] !== undefined;
              const currentStatusCoord = isModifiedCoord ? modifiedProfesseurs[prof.id] : prof.coordonateur;
              const currentStatusDelegue = isModifiedDelegue ? modifiedDelegues[prof.id] : (prof.delegue_affectation ?? false);
              const classNames = getClassNames(prof.classes);

              return (
                <div key={prof.id} className="relative group">
                  {/* Glow effect */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur opacity-0 group-hover:opacity-20 transition duration-300" />

                  {/* Card avec glassmorphism */}
                  <div className="relative bg-white/70 backdrop-blur-xl border border-white/50 rounded-2xl p-5 xl:p-3.5 2xl:p-4 3xl:p-5 shadow-lg hover:shadow-xl transition-all duration-300">
                    {/* Header de la card */}
                    <div className="flex items-start justify-between mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                      <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                        <div className="w-12 h-12 xl:w-9 xl:h-9 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-br from-blue-500 to-cyan-500 shadow-blue-500/30">
                          <User className="w-6 h-6 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 text-lg xl:text-sm 2xl:text-base 3xl:text-lg">{prof.nom}</p>
                          <p className="text-slate-500 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">{prof.prenom}</p>
                        </div>
                      </div>

                      {/* Badge matière */}
                      <span className="inline-flex items-center px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1 rounded-full text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold bg-cyan-100 text-cyan-700">
                        {formatMatiere(prof.matiere)}
                      </span>
                    </div>

                    {/* Classes */}
                    <div className="mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                      <p className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-slate-400 uppercase tracking-wide mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">Classes</p>
                      <div className="flex flex-wrap gap-1.5">
                        {classNames.length > 0 ? classNames.map((className, idx) => (
                          <span key={idx} className="inline-block bg-slate-100 text-slate-600 px-2.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-1 rounded-lg text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium">
                            {className}
                          </span>
                        )) : (
                          <span className="text-slate-400 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">Aucune classe</span>
                        )}
                      </div>
                    </div>

                    {/* Statuts */}
                    <div className="flex items-center gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4 mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4 p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-slate-50/80 rounded-xl">
                      <div className="flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                        {currentStatusCoord ? (
                          <>
                            <Award className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 text-amber-500" />
                            <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-amber-700">Coord.</span>
                          </>
                        ) : (
                          <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-400">Non coord.</span>
                        )}
                        {isModifiedCoord && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded-full font-medium">modifié</span>
                        )}
                      </div>

                      <div className="w-px h-6 xl:h-5 2xl:h-5 3xl:h-6 bg-slate-200" />

                      <div className="flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                        {currentStatusDelegue ? (
                          <>
                            <UserCog className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 text-emerald-500" />
                            <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-emerald-700">Délég.</span>
                          </>
                        ) : (
                          <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-400">Non délég.</span>
                        )}
                        {isModifiedDelegue && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded-full font-medium">modifié</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 xl:gap-1.5 2xl:gap-2 3xl:gap-2">
                      {/* Modif en attente → le bouton devient une ANNULATION
                          explicite (cerclé orange) : « Nommer » après un
                          « Retirer » laissait croire à une invite, alors que
                          le clic ne fait que revenir à l'état sauvé */}
                      <button
                        onClick={() => toggleCoordonnateur(prof.id)}
                        className={`flex-1 px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-2.5 xl:py-2 2xl:py-2 3xl:py-2.5 rounded-xl text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium transition-all duration-200 ${isModifiedCoord
                            ? "bg-orange-50 text-orange-700 ring-2 ring-orange-400 hover:bg-orange-100"
                            : currentStatusCoord
                              ? "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              : "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40"
                          }`}
                      >
                        {isModifiedCoord ? "Annuler" : currentStatusCoord ? "Retirer coord." : "Nommer coord."}
                      </button>
                      <button
                        onClick={() => toggleDelegue(prof.id)}
                        className={`flex-1 px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-2.5 xl:py-2 2xl:py-2 3xl:py-2.5 rounded-xl text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium transition-all duration-200 ${isModifiedDelegue
                            ? "bg-orange-50 text-orange-700 ring-2 ring-orange-400 hover:bg-orange-100"
                            : currentStatusDelegue
                              ? "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              : "bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40"
                          }`}
                      >
                        {isModifiedDelegue ? "Annuler" : currentStatusDelegue ? "Retirer délég." : "Nommer délég."}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Note d'information */}
        <div className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 mt-6 xl:mt-4 2xl:mt-5 3xl:mt-6 relative">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-300 to-cyan-300 rounded-2xl blur opacity-20" />
          <div className="relative bg-blue-50/80 backdrop-blur-xl border border-blue-200/50 rounded-2xl p-4 xl:p-3 2xl:p-3.5 3xl:p-4">
            <div className="flex items-start gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
              <div className="p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex-shrink-0">
                <AlertCircle className="w-4 h-4 xl:w-3.5 xl:h-3.5 2xl:w-3.5 2xl:h-3.5 3xl:w-4 3xl:h-4 text-white" />
              </div>
              <div className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-blue-800">
                <p className="font-semibold mb-1">Information</p>
                <p className="text-blue-700">En général, un seul professeur par discipline est coordonnateur. Si plusieurs coordonnateurs sont désignés, assurez-vous que cela correspond à l'organisation de votre établissement.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Boutons de validation flottants */}
        {hasChanges && (
          <div className="px-4 fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500 rounded-2xl blur opacity-75 animate-pulse" />
              <div className="relative flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 bg-white/95 backdrop-blur-xl border border-white/50 rounded-2xl p-3 xl:p-2 2xl:p-2.5 3xl:p-3 shadow-2xl">
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 xl:gap-1.5 2xl:gap-2 3xl:gap-2 px-6 xl:px-4 2xl:px-5 3xl:px-6 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl hover:from-emerald-600 hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium text-sm xl:text-xs 2xl:text-xs 3xl:text-sm"
                >
                  <Save className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                  Valider les changements
                </button>
                <button
                  onClick={cancelChanges}
                  className="flex items-center gap-2 xl:gap-1.5 2xl:gap-2 3xl:gap-2 px-6 xl:px-4 2xl:px-5 3xl:px-6 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-medium text-sm xl:text-xs 2xl:text-xs 3xl:text-sm"
                >
                  <X className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Confirmations (valider, quitter sans valider) — à la racine pour
          échapper aux contextes d'empilement des conteneurs */}
      {ConfirmDialog}
    </div>
  );
};

export default ProfesseurManagement;
